import { useState } from "react";
import {
  ArrowDownToLine,
  ArrowRight,
  BookOpen,
  CloudOff,
  Compass,
  FlaskConical,
  ShieldCheck,
  UsersRound,
  Waves,
  TrainFront,
  Zap,
  ScanLine,
} from "lucide-react";
import { CityMap } from "./components/CityMap";
import { ExplorePanel } from "./components/ExplorePanel";
import { SimulatePanel } from "./components/SimulatePanel";
import { RespondPanel } from "./components/RespondPanel";
import { ResidentPanel } from "./components/ResidentPanel";
import { Signal, Metrics } from "./components/Signal";
import { Timeline } from "./components/Timeline";
import { PulseMark } from "./components/Primitives";
import { OutcomeDialog } from "./components/OutcomeDialog";
import { HelpDialog } from "./components/HelpDialog";
import { useCity, type Layer, type Mode } from "./state/CityContext";
import { usePwa } from "./lib/usePwa";
import { SCENARIOS } from "./data/city";
const modes: { id: Mode; name: string; Icon: typeof Compass }[] = [
  { id: "explore", name: "Explore", Icon: Compass },
  { id: "simulate", name: "Simulate", Icon: FlaskConical },
  { id: "respond", name: "Respond", Icon: ShieldCheck },
];
const layers: { id: Layer; name: string; Icon: typeof Waves }[] = [
  { id: "activity", name: "City", Icon: ScanLine },
  { id: "water", name: "Water", Icon: Waves },
  { id: "transport", name: "Mobility", Icon: TrainFront },
  { id: "power", name: "Energy", Icon: Zap },
];
export default function App() {
  const {
    state,
    dispatch,
    current,
    saveStatus,
    restored,
    notice,
    clearNotice,
  } = useCity();
  const [dialog, setDialog] = useState<"help" | "outcome" | null>(null),
    [restoreHidden, setRestoreHidden] = useState(false);
  const pwa = usePwa();
  const navigate = (mode: Mode) => {
    dispatch({ type: "mode", mode });
    dispatch({ type: "perspective", perspective: "operator" });
    if (window.matchMedia("(max-width:760px)").matches)
      requestAnimationFrame(() =>
        document
          .querySelector(".mode-panel-shell")
          ?.scrollIntoView({
            block: "start",
            behavior: window.matchMedia("(prefers-reduced-motion:reduce)")
              .matches
              ? "instant"
              : "smooth",
          }),
      );
  };
  return (
    <div className={`app mode-${state.mode} perspective-${state.perspective}`}>
      <a className="skip-link" href="#main">
        Skip to city controls
      </a>
      <header className="app-header">
        <button
          className="brand"
          aria-label="PULSE home, explore Lichfield"
          onClick={() => {
            dispatch({ type: "mode", mode: "explore" });
            dispatch({ type: "perspective", perspective: "operator" });
          }}
        >
          <PulseMark />
          <span>
            PULSE<small>A LIVING CITY</small>
          </span>
        </button>
        <nav className="mode-nav" aria-label="City mode">
          {modes.map(({ id, name, Icon }, i) => (
            <button
              key={id}
              aria-current={state.mode === id ? "page" : undefined}
              className={state.mode === id ? "active" : ""}
              onClick={() => navigate(id)}
            >
              <span className="nav-number">0{i + 1}</span>
              <Icon size={17} />
              <span>{name}</span>
              {id === "respond" && current.incidents.length > 0 && (
                <span className="nav-count">{current.incidents.length}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="header-tools">
          <div className="perspective-switch" aria-label="Perspective">
            <button
              aria-pressed={state.perspective === "operator"}
              className={state.perspective === "operator" ? "active" : ""}
              onClick={() =>
                dispatch({ type: "perspective", perspective: "operator" })
              }
            >
              <ShieldCheck size={14} />
              <span>Operator</span>
            </button>
            <button
              aria-pressed={state.perspective === "resident"}
              className={state.perspective === "resident" ? "active" : ""}
              onClick={() =>
                dispatch({ type: "perspective", perspective: "resident" })
              }
            >
              <UsersRound size={14} />
              <span>Resident</span>
            </button>
          </div>
          <button
            className="icon-button install-control"
            aria-label="Install PULSE"
            title="Install PULSE"
            onClick={() => {
              if (pwa.canInstall) void pwa.installApp();
              else setDialog("help");
            }}
          >
            <ArrowDownToLine size={17} />
          </button>
          <button
            className="icon-button"
            aria-label="Open field notes, model and device options"
            title="Field notes and model"
            onClick={() => setDialog("help")}
          >
            <BookOpen size={18} />
          </button>
        </div>
      </header>
      <main id="main" className="city-stage">
        <CityMap />
        <div className="city-title">
          <span className="eyebrow">STAFFORDSHIRE / UNITED KINGDOM</span>
          <h2>
            Lichfield<span>52.685° N / 1.826° W</span>
          </h2>
        </div>
        <div className="layer-switch" aria-label="Map layer">
          {layers.map(({ id, name, Icon }) => (
            <button
              key={id}
              aria-pressed={state.layer === id}
              className={state.layer === id ? "active" : ""}
              onClick={() => dispatch({ type: "layer", layer: id })}
            >
              <Icon size={14} />
              {name}
            </button>
          ))}
        </div>
        <div className="mode-panel-shell">
          <div className="mobile-panel-bar">
            <span>
              {state.run.minute} MIN / {Math.round(current.continuity)}%
              CONTINUITY
            </span>
            <a href="#main">
              Back to atlas <Compass size={12} />
            </a>
          </div>
          {state.perspective === "resident" ? (
            <ResidentPanel />
          ) : state.mode === "explore" ? (
            <ExplorePanel />
          ) : state.mode === "simulate" ? (
            <SimulatePanel />
          ) : (
            <RespondPanel onOutcome={() => setDialog("outcome")} />
          )}
        </div>
        <Signal />
        <div className="map-context">
          <span className="context-line" />
          <div>
            <span className="eyebrow">
              {state.run.scenario === "calm"
                ? "THE EVERYDAY CITY"
                : "EXPERIMENT IN PROGRESS"}
            </span>
            <p>{SCENARIOS[state.run.scenario].name}</p>
          </div>
          <span className="context-tag">
            {state.run.scenario === "calm"
              ? "SYNTHETIC ACTIVITY"
              : "FICTIONAL SCENARIO"}
          </span>
        </div>
        <div className="bottom-console">
          <Metrics />
          <Timeline onOutcome={() => setDialog("outcome")} />
        </div>
      </main>
      <footer className="app-footer">
        <span className="footer-location">
          <span className="live-dot" /> LICHFIELD CITY ATLAS
        </span>
        <span className="prototype-notice">
          EXPERIMENTAL PROTOTYPE · ALL SERVICE DATA SIMULATED
        </span>
        <span className="storage-status">
          {!pwa.online ? (
            <>
              <CloudOff size={12} />
              Offline
            </>
          ) : pwa.offlineReady ? (
            <>
              <ShieldCheck size={12} />
              Offline ready
            </>
          ) : null}
          <i />
          {saveStatus === "saved"
            ? "Saved on this device"
            : saveStatus === "memory"
              ? "Session in memory"
              : saveStatus === "loading"
                ? "Restoring…"
                : "Saving…"}
        </span>
      </footer>
      {notice && (
        <div className="restore-toast response-toast" role="status">
          <span>{notice}</span>
          <button onClick={clearNotice} aria-label="Dismiss crew notice">
            <ArrowRight size={15} />
          </button>
        </div>
      )}
      {restored && !restoreHidden && !notice && (
        <div className="restore-toast" role="status">
          <span>Your last experiment is restored.</span>
          <button
            onClick={() => setRestoreHidden(true)}
            aria-label="Dismiss restored experiment notice"
          >
            <ArrowRight size={15} />
          </button>
        </div>
      )}
      {pwa.update && (
        <div className="update-toast">
          <span>A new PULSE version is ready.</span>
          <button onClick={pwa.updateApp}>Update</button>
        </div>
      )}
      {dialog === "outcome" && (
        <OutcomeDialog onClose={() => setDialog(null)} />
      )}
      {dialog === "help" && (
        <HelpDialog
          onClose={() => setDialog(null)}
          offlineReady={pwa.offlineReady}
          canInstall={pwa.canInstall}
          installApp={pwa.installApp}
        />
      )}
    </div>
  );
}
